// French copy, namespace `common`: words and messages shared by every screen
// (API client errors, dates, page states, language picker).

import type { ReactNode } from "react";

export const common = {
  network: {
    offline: "Connexion impossible : vérifiez votre réseau, puis réessayez.",
    expired: "Votre session a expiré. Reconnectez-vous.",
    forbidden: "Cette action ne vous est pas ouverte.",
    notFound: "Introuvable.",
    unavailable: "Le service ne répond pas pour le moment. Réessayez dans un instant.",
    failed: "La demande n’a pas abouti.",
  },
  time: {
    justNow: "à l’instant",
    today: "aujourd’hui",
  },
  language: {
    label: "Langue",
    description: "La langue de l’app, des e-mails de Hune et de ce que rédige l’agent (rapports, dossiers, messages).",
  },
  pageTitles: {
    notFound: "Page introuvable — Hune",
    error: "Erreur — Hune",
  },
  /** Browser tab title while the agent runs a pass. */
  tab: {
    running: (title: string) => `Analyse en cours… — ${title}`,
  },
  states: {
    overview: "Vue d’ensemble",
    retry: "Réessayer",
    reload: "Recharger la page",
    /** « Impossible de charger » + what is missing (« vos leads », « ce rapport »…), given by the page. */
    loadFailed: (subject: string) => `Impossible de charger ${subject}.`,
    loading: (subject: string) => `Chargement de ${subject}…`,
    loadInterrupted: "Un incident a interrompu le chargement.",
    nothingLost: "Rien n’est perdu : vos données restent enregistrées.",
    contact: (email: ReactNode): ReactNode[] => ["Si le problème persiste, écrivez-nous à ", email, "."],
    thisPage: "cette page",
    yourWorkspace: "votre espace",
    notFound: {
      title: "Page introuvable.",
      body: "Cette adresse ne mène à aucun écran de votre espace.",
    },
    notFoundPage: {
      title: "Page introuvable.",
      body: "Cette adresse ne mène nulle part.",
      back: "Retour à votre espace",
    },
    renderError: {
      title: "Cette page n’a pas pu s’afficher.",
      body: "Un incident a interrompu l’affichage. Vos données ne sont pas touchées : rechargez la page pour reprendre.",
    },
    /** Root error screen of both routers (shared/client/route-error.tsx). */
    crash: {
      title: "Cette page a planté.",
      body: "Rechargez-la. Si l’erreur revient, dites-le-nous : nous la corrigerons.",
    },
  },
  /** Fixed labels of the loading skeletons, the same as on the screens they stand for. */
  skeletons: {
    activity: (days: number) => `Activité des ${days} derniers jours`,
    comparedWith: (days: number) => `Comparée aux ${days} jours précédents`,
    recentPasses: "Derniers passages",
    pipeline: "Pipeline",
    leadsSource: "Alimenté par chaque passage de votre veille",
    organization: "Organisation",
    signal: "Signal",
    contact: "Interlocuteur",
    score: "Score",
    status: "Statut",
    history: "Historique",
    pass: "Passage",
    startedAt: "Lancé le",
    state: "État",
    report: "Rapport",
    targeting: "Ciblage",
    monitoring: "Veille",
  },
  consent: {
    settings: "Gestion des cookies",
    bannerTitle: "Vos choix en matière de cookies",
    bannerText:
      "Hune utilise des cookies nécessaires au fonctionnement du site et, avec votre accord, Google Analytics pour mesurer son audience. Votre choix vaut aussi pour le site hune-app.com, et reste modifiable depuis « Gestion des cookies » (écran de connexion, paramètres du compte).",
    privacyLink: "Politique de confidentialité",
    customize: "Personnaliser",
    refuseAll: "Tout refuser",
    acceptAll: "Tout accepter",
    panelText:
      "Activez ou désactivez chaque catégorie. Votre choix vaut pour l’application et le site, est conservé six mois et reste modifiable à tout moment.",
    necessary: "Nécessaires",
    necessaryText: "Connexion à votre compte, sécurité, mémorisation de l’offre consultée et de vos choix ci-dessous.",
    alwaysOn: "Toujours actifs",
    internalStats: "Statistiques internes",
    internalStatsText:
      "Mesure d’audience anonyme réalisée par Hune, sans transmission à des tiers. Exemptée de consentement, vous pouvez néanmoins vous y opposer.",
    analyticsText: "Mesure d’audience par Google (cookies _ga, 13 mois au plus). Déposés uniquement avec votre accord.",
    save: "Enregistrer mes choix",
  },
};
